import { useSetAtom } from "jotai";
import { type Ref, useImperativeHandle, useRef } from "react";

import { AbsenceDialog } from "./components/AbsenceDialog";
import { hideAbsenceManagerAtom, showAbsenceManagerAtom } from "./stores";

/**
 * What the caller already knows about the employee. The row that opens the
 * manager holds both, so the header can be drawn before any request lands.
 */
export interface AbsenceManagerEmployee {
  name?: string;
  avatarUrl?: string | null;
}

export interface AbsenceManagerHandle {
  open: (
    employeeId: number,
    employee?: AbsenceManagerEmployee,
    originElement?: HTMLElement,
  ) => AbsenceManagerHandle;
  hide: () => AbsenceManagerHandle;
}

export type AbsenceManagerProps = {
  onSaved?: () => void;
  ref?: Ref<AbsenceManagerHandle>;
};

export function AbsenceManager({ onSaved, ref }: AbsenceManagerProps) {
  const show = useSetAtom(showAbsenceManagerAtom);
  const hide = useSetAtom(hideAbsenceManagerAtom);
  const originRef = useRef<DOMRect | null>(null);

  const handle: AbsenceManagerHandle = {
    open: (employeeId, employee, originElement) => {
      originRef.current = originElement?.getBoundingClientRect() ?? null;

      show(employeeId, employee?.name ?? "", employee?.avatarUrl ?? null);

      return handle;
    },
    hide: () => {
      hide();

      return handle;
    },
  };

  useImperativeHandle(ref, () => handle, []);

  return <AbsenceDialog onSaved={onSaved} originRef={originRef} />;
}
